// v2 Overview / Home page at route "/" (CHG-1238).
//
// Thin consumer of DashboardPage + 5 content primitives. All data flows
// through fetchOverview() in one round-trip. Replaces the CHG-1167
// PlaceholderIndex.

import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import { StatStrip, type StatStripItem } from "../components/composition/StatStrip";
import { SetupProgress } from "../components/composition/SetupProgress";
import { DonationsFeed } from "../components/composition/DonationsFeed";
import { AICallout } from "../components/composition/AICallout";
import { fetchOverview, type OverviewStats } from "../api/overview";
// CHG-1274 — ACI runtime hook-swap for surface 1 (overview-suggestions).
// Replaces the static bundled `data.ai_suggestions` source with a fresh
// useACISuggestions call. AICallout itself UNCHANGED — only its data
// source changes. Falls back to the bundled overview.ai_suggestions when
// ACI returns empty (forward-promise period while backend isn't yet
// deployed). When ACI ships real suggestions, those take precedence.
import { useACISuggestions } from "../components/aci/useACISuggestions";

function statsToItems(stats: OverviewStats | undefined): StatStripItem[] {
  const currency = stats?.primary_currency;
  return [
    { id: "today", label: "Today", value: stats?.today_minor_units, format: "currency", currency, tone: "g-home" },
    { id: "week", label: "Last 7 days", value: stats?.week_minor_units, format: "currency", currency, tone: "g-giving" },
    { id: "month", label: "Last 30 days", value: stats?.month_minor_units, format: "currency", currency, tone: "g-giving" },
    { id: "ytd", label: "Year to date", value: stats?.ytd_minor_units, format: "currency", currency, tone: "g-giving" },
    { id: "mrr", label: "Recurring MRR", value: stats?.recurring_mrr_minor_units, format: "currency", tone: "g-automation" },
    { id: "campaigns", label: "Active campaigns", value: stats?.active_campaigns_count, format: "number", tone: "g-events" },
  ];
}

export function Overview() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["overview"],
    queryFn: fetchOverview,
  });

  // CHG-1274 — fetch ACI suggestions via the runtime endpoint; safeFetch
  // wrapper returns [] on 404 so we fall back to the bundled set below.
  const { suggestions: aciSuggestions } = useACISuggestions({
    surface_id: "overview-suggestions",
  });
  const showSetup = !!data && !data.setup_complete && data.setup_steps.length > 0;
  // Prefer real ACI suggestions when available; fall back to the bundled
  // overview.ai_suggestions array (CHG-1238 source) until the ACI backend
  // is deployed. Both arrays share the same AISuggestion shape (per
  // CHG-1250 §7.2 type contract).
  const effectiveSuggestions =
    aciSuggestions.length > 0 ? aciSuggestions : (data?.ai_suggestions ?? []);
  const showCallout = effectiveSuggestions.length > 0;

  return (
    <DashboardPage
      title="Overview"
      subtitle="What's happening today"
      // CHG-1405 / ITER-3524 — header actions repointed to the mounted list
      // pages (audit F-01). `/donations/new` and `/campaigns/new` are not
      // mounted in App.tsx; each list page emptyState (audit V-01) already
      // invites the next step on a freshly-seeded org. Dedicated `*/new`
      // forms remain a forward-promise for a later iteration; spec:
      // specs/_product/dashboard-v2-onboarding-polish-audit.md.
      headerActions={[
        { id: "record-donation", label: "Record donation", variant: "primary", href: "/donations" },
        { id: "create-campaign", label: "Create campaign", variant: "secondary", href: "/campaigns" },
      ]}
      setupProgress={showSetup ? <SetupProgress steps={data!.setup_steps} /> : null}
      stats={<StatStrip items={statsToItems(data?.stats)} loading={isLoading} />}
      feed={<DonationsFeed donations={data?.recent_donations ?? []} loading={isLoading} />}
      callout={showCallout ? <AICallout suggestions={effectiveSuggestions} /> : null}
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
